import { useEffect, useState } from 'react';
import { createPortal } from 'react-dom';
import { useDispatch, useSelector } from 'react-redux';
import { useTranslation } from 'react-i18next';
import { toast } from 'react-toastify';
import { X, Share2, Eye, Pencil, Trash2, Loader2, ArrowDownLeft, ArrowUpRight } from 'lucide-react';
import { getShares, updateShare, revokeShare } from '../../redux/familySlice';

// Who can open MY account — the owner's own screen.
//
// TWO LISTS, NOT ONE. Sharing goes one way, so "people I gave access to" and
// "accounts I can open" are different facts about different records. Showing
// them as a single list would suggest access is mutual, which is exactly the
// thing this whole model exists to stop being true.
//
// The owner needs nobody's approval here: raising, lowering and removing all
// take effect immediately, because it is their record. Anything requiring
// someone else's agreement lives in the request flow instead.
export default function SharingSettings({ isOpen, onClose }) {
    const { t } = useTranslation();
    const dispatch = useDispatch();
    const { sharedWithOthers, sharedWithMe, sharesLoading } = useSelector((s) => s.family || {});
    const [busy, setBusy] = useState(null);
    const [confirming, setConfirming] = useState(null);

    useEffect(() => { if (isOpen) dispatch(getShares()); }, [isOpen, dispatch]);

    if (!isOpen) return null;

    const act = async (thunk, arg, okMsg) => {
        setBusy(arg?.shareId || arg);
        setConfirming(null);
        try {
            const res = await dispatch(thunk(arg)).unwrap();
            toast.success(res?.message?.en || okMsg);
        } catch (err) {
            const msg = err?.en || err?.message?.en || (typeof err === 'string' ? err : null);
            toast.error(msg || t('family.action_failed', 'That did not work'));
        } finally { setBusy(null); }
    };

    const avatar = (row) => (
        row.image
            ? <img src={row.image} alt="" className="h-8 w-8 flex-shrink-0 rounded-full object-cover" />
            : (
                <span className="flex h-8 w-8 flex-shrink-0 items-center justify-center rounded-full bg-[#4ABDBD]/15 text-xs font-bold text-[#3AA8A8]">
                    {(row.name || '?').charAt(0).toUpperCase()}
                </span>
            )
    );

    const typeLabel = (row) => (
        row.accountType === 'secondary'
            ? t('family.secondary', 'Secondary')
            : t('family.primary', 'Primary')
    );

    return createPortal(
        <div className="fixed inset-0 z-[9999] flex items-center justify-center bg-black/50 p-4">
            <div data-testid="sharing-settings" className="w-full max-w-md rounded-2xl bg-white shadow-xl">
                <div className="flex items-center justify-between border-b border-gray-100 px-5 py-4">
                    <h3 className="flex items-center gap-2 font-bold text-[#1a365d]">
                        <Share2 className="h-4 w-4 text-[#4ABDBD]" />
                        {t('family.sharing', 'Sharing')}
                    </h3>
                    <button type="button" onClick={onClose} className="cursor-pointer text-gray-400 hover:text-red-500">
                        <X className="h-5 w-5" />
                    </button>
                </div>

                <div className="max-h-[65vh] overflow-y-auto px-4 py-4">
                    {sharesLoading && (
                        <div className="py-8 text-center"><Loader2 className="mx-auto h-5 w-5 animate-spin text-[#4ABDBD]" /></div>
                    )}

                    {/* ---- who can open MY account ---- */}
                    <p className="mb-1 flex items-center gap-1.5 text-[11px] font-bold uppercase tracking-wide text-gray-400">
                        <ArrowUpRight className="h-3 w-3" />
                        {t('family.can_open_mine', 'Who can open my account')}
                    </p>
                    <p className="mb-2.5 text-[11px] text-gray-500">
                        {t('family.can_open_mine_note', 'You can change or remove this at any time. Nobody has to agree.')}
                    </p>

                    {!sharesLoading && (sharedWithOthers || []).length === 0 && (
                        <p className="mb-5 rounded-xl bg-gray-50 px-3 py-3 text-[11px] text-gray-500">
                            {t('family.nobody_can_open', 'Nobody else can open your account.')}
                        </p>
                    )}

                    {(sharedWithOthers || []).map((row) => (
                        <div key={row.shareId} data-testid={`share-${row.shareId}`}
                            className="mb-1.5 rounded-xl border border-gray-200 p-2.5">
                            <div className="flex items-center gap-2.5">
                                {avatar(row)}
                                <span className="min-w-0 flex-1">
                                    <span className="block truncate text-sm font-semibold text-gray-800">{row.name}</span>
                                    <span className="block text-[10px] text-gray-400">{typeLabel(row)}</span>
                                </span>
                            </div>

                            <div className="mt-2 flex gap-1.5">
                                {/* Two buttons rather than a dropdown: the current
                                    level should be readable without opening
                                    anything, since it decides what someone else
                                    may do to this record. */}
                                <button type="button" disabled={busy === row.shareId}
                                    onClick={() => row.permission !== 'read' && act(updateShare, { shareId: row.shareId, permission: 'read' }, t('family.access_updated', 'Access updated'))}
                                    className={`flex flex-1 cursor-pointer items-center justify-center gap-1 rounded-lg border py-1.5 text-[11px] font-semibold transition disabled:opacity-50 ${
                                        row.permission === 'read'
                                            ? 'border-[#4ABDBD] bg-[#4ABDBD]/10 text-[#3AA8A8]'
                                            : 'border-gray-200 text-gray-500 hover:bg-gray-50'}`}>
                                    <Eye className="h-3 w-3" />
                                    {t('family.read', 'Read')}
                                </button>
                                <button type="button" disabled={busy === row.shareId}
                                    onClick={() => row.permission !== 'readwrite' && act(updateShare, { shareId: row.shareId, permission: 'readwrite' }, t('family.access_updated', 'Access updated'))}
                                    className={`flex flex-1 cursor-pointer items-center justify-center gap-1 rounded-lg border py-1.5 text-[11px] font-semibold transition disabled:opacity-50 ${
                                        row.permission === 'readwrite'
                                            ? 'border-[#4ABDBD] bg-[#4ABDBD]/10 text-[#3AA8A8]'
                                            : 'border-gray-200 text-gray-500 hover:bg-gray-50'}`}>
                                    <Pencil className="h-3 w-3" />
                                    {t('family.read_write', 'Read & write')}
                                </button>
                                <button type="button" disabled={busy === row.shareId}
                                    onClick={() => setConfirming(row.shareId)}
                                    className="cursor-pointer rounded-lg border border-red-200 px-2.5 py-1.5 text-red-500 transition hover:bg-red-50 disabled:opacity-50">
                                    {busy === row.shareId ? <Loader2 className="h-3 w-3 animate-spin" /> : <Trash2 className="h-3 w-3" />}
                                </button>
                            </div>

                            {confirming === row.shareId && (
                                <div className="mt-2 rounded-lg border border-red-200 bg-red-50 p-2.5">
                                    <p className="text-[11px] text-red-900">
                                        {t('family.revoke_confirm', 'Remove their access? They will no longer be able to open your account.')}
                                    </p>
                                    <div className="mt-2 flex gap-2">
                                        <button type="button" data-testid={`revoke-${row.shareId}`}
                                            onClick={() => act(revokeShare, row.shareId, t('family.access_removed', 'Access removed'))}
                                            className="flex-1 cursor-pointer rounded-lg bg-red-600 py-1.5 text-[10px] font-semibold text-white hover:bg-red-700">
                                            {t('family.remove', 'Remove')}
                                        </button>
                                        <button type="button" onClick={() => setConfirming(null)}
                                            className="flex-1 cursor-pointer rounded-lg border border-gray-200 bg-white py-1.5 text-[10px] font-semibold text-gray-600 hover:bg-gray-50">
                                            {t('common.cancel', 'Cancel')}
                                        </button>
                                    </div>
                                </div>
                            )}
                        </div>
                    ))}

                    {/* ---- what I can open ---- */}
                    <p className="mb-1 mt-5 flex items-center gap-1.5 text-[11px] font-bold uppercase tracking-wide text-gray-400">
                        <ArrowDownLeft className="h-3 w-3" />
                        {t('family.i_can_open', 'Accounts I can open')}
                    </p>
                    <p className="mb-2.5 text-[11px] text-gray-500">
                        {t('family.i_can_open_note', 'Shared with you by someone else. They decide the level — you can give it up at any time.')}
                    </p>

                    {!sharesLoading && (sharedWithMe || []).length === 0 && (
                        <p className="rounded-xl bg-gray-50 px-3 py-3 text-[11px] text-gray-500">
                            {t('family.none_shared_with_me', 'Nobody has shared their account with you.')}
                        </p>
                    )}

                    {(sharedWithMe || []).map((row) => (
                        <div key={row.shareId} className="mb-1.5 flex items-center gap-2.5 rounded-xl border border-gray-200 p-2.5">
                            {avatar(row)}
                            <span className="min-w-0 flex-1">
                                <span className="block truncate text-sm font-semibold text-gray-800">{row.name}</span>
                                <span className="block text-[10px] text-gray-400">
                                    {typeLabel(row)} · {row.permission === 'readwrite'
                                        ? t('family.read_write', 'Read & write')
                                        : t('family.read_only', 'Read only')}
                                </span>
                            </span>
                            {/* Nobody should be forced to hold access to somebody
                                else's medical record — but NOT for a secondary.
                                A secondary cannot sign in, so walking away from it
                                does not release you from someone else's record; it
                                either strands the account or quietly makes it
                                somebody else's responsibility. To stop managing
                                one, give it its own login first (Switch profile ->
                                Edit -> Split). */}
                            {row.accountType === 'secondary' ? (
                                <span className="rounded-lg bg-gray-50 px-2 py-1 text-[10px] font-medium text-gray-400">
                                    {t('family.managed_by_you', 'You manage this')}
                                </span>
                            ) : (
                                <button type="button" disabled={busy === row.shareId}
                                    onClick={() => act(revokeShare, row.shareId, t('family.access_given_up', 'You no longer have access'))}
                                    className="cursor-pointer rounded-lg border border-gray-200 px-2 py-1 text-[10px] font-semibold text-gray-500 transition hover:bg-gray-50 disabled:opacity-50">
                                    {busy === row.shareId ? <Loader2 className="h-3 w-3 animate-spin" /> : t('family.give_up', 'Give up')}
                                </button>
                            )}
                        </div>
                    ))}
                </div>
            </div>
        </div>,
        document.body
    );
}
